import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  HelpTip,
  Input,
} from '@/components/ui';
import { api } from '@/lib/api';

/**
 * O editor de um aviso do WhatsApp.
 *
 * 🔴 **A pré-visualização não é enfeite: é a única forma de conferir antes.**
 * Depois que o gatilho liga, a próxima leitura do texto é a do síndico no
 * grupo do condomínio. Aqui o Matheus vê a frase montada com dados de exemplo —
 * exatamente o que sai — e só então liga.
 *
 * ⚠️ As variáveis entram **por clique, na posição do cursor**, e não digitadas.
 * `{{tecncio}}` com erro de digitação nunca teria valor e só apareceria no
 * envio, depois de o gatilho estar ligado e o caso ter acontecido. Digitar à
 * mão continua possível, e aí o servidor recusa ao salvar dizendo qual variável
 * não existe.
 */

interface Variavel {
  chave: string;
  rotulo: string;
  exemplo: string;
}

interface Detalhe {
  chave: string;
  nome: string;
  descricao: string;
  ligado: boolean;
  v1AindaFaz: boolean;
  v1Desligado: boolean;
  template: string;
  variaveis: Variavel[];
  /**
   * 🔴 Alarme da própria IGM (frente 9): nunca alcança grupo de cliente. A
   * tela precisa dizer isso, porque quem escreve escolhe o tom pelo leitor.
   */
  soInterno: boolean;
  previa: string;
  /**
   * 🔴 A LINHA POR EQUIPAMENTO (mig 580) — só nos avisos que listam
   * equipamento. `atualVolta` é null quando o aviso não tem "voltou ao ar".
   */
  linha?: {
    atual: string;
    atualVolta: string | null;
    variaveis: Variavel[];
    teto: number;
  };
  /**
   * 🔴 OS BLOCOS EXTRAS (adendo 11/09): texto que o servidor ANEXA quando o
   * caso acontece — o chamado vinculado à OS, os equipamentos que o cliente já
   * sabia. Não são variáveis do modelo de propósito: variável vazia barra a
   * mensagem inteira, e estes casos são raros.
   *
   * ⚠️ **Campo vazio = bloco desligado.**
   */
  blocos?: Array<{
    chave: string;
    rotulo: string;
    ajuda: string;
    variaveis: Variavel[];
    atual: string;
  }>;
}

/**
 * Três equipamentos de exemplo, com horários DIFERENTES — sem isso a prévia
 * não mostra que cada um tem o seu.
 *
 * ⚠️ Os MESMOS que o servidor usa (`EXEMPLOS_DA_LISTA` em
 * `lista-de-equipamentos.ts`): a prévia da tela e a do servidor têm de contar
 * a mesma história. Duplicados aqui porque o front não importa do servidor —
 * se um dia divergirem, é este comentário que diz onde olhar.
 */
const EXEMPLOS_DA_LISTA = [
  { equipamento: 'CÂMERA 01', desde: '04/09/2026 às 13:47', duracao: '18 min', detalhe: '5 quedas nas últimas 24h', endereco: '192.168.0.31', tipo: 'Câmera' },
  { equipamento: 'CÂMERA 02', desde: '04/09/2026 às 13:50', duracao: '2 h 05', detalhe: '3 quedas nas últimas 24h', endereco: '192.168.0.32', tipo: 'Câmera' },
  { equipamento: 'DVR DA PORTARIA', desde: '04/09/2026 às 13:47', duracao: '18 min', detalhe: '9 quedas nas últimas 24h', endereco: '192.168.0.10', tipo: 'DVR' },
] as Array<Record<string, string>>;

/** A lista montada como o servidor monta: uma linha por equipamento. */
function listaDeExemplo(linha: string): string {
  const t = linha.trim();
  if (!t) return 'CÂMERA 01, CÂMERA 02 e DVR DA PORTARIA';
  return EXEMPLOS_DA_LISTA.map((eq) => {
    const texto = t.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_todo, nome: string) => eq[nome] ?? '');
    return texto.replace(/[ \t]+/g, ' ').trim() || eq.equipamento;
  }).join('\n');
}

/**
 * O WhatsApp entende `*negrito*`, `_itálico_` e `~riscado~`. A prévia mostra o
 * texto **como o cliente vê**, não a marcação crua — senão o Matheus confere
 * uma coisa e o cliente recebe outra.
 *
 * 🔴 Devolve NÓS DO REACT, nunca HTML. O texto vem de um campo digitado e vai
 * ser exibido numa tela de admin; montar HTML a partir dele abriria uma porta
 * de injeção por um ganho puramente cosmético.
 */
function comFormatacao(texto: string): React.ReactNode[] {
  const partes: React.ReactNode[] = [];
  const re = /(\*[^*\n]+\*)|(_[^_\n]+_)|(~[^~\n]+~)/g;
  let ultimo = 0;
  let m: RegExpExecArray | null;
  let i = 0;

  while ((m = re.exec(texto)) !== null) {
    if (m.index > ultimo) partes.push(texto.slice(ultimo, m.index));
    const bruto = m[0];
    const miolo = bruto.slice(1, -1);
    if (bruto.startsWith('*')) partes.push(<strong key={i++}>{miolo}</strong>);
    else if (bruto.startsWith('_')) partes.push(<em key={i++}>{miolo}</em>);
    else partes.push(<s key={i++}>{miolo}</s>);
    ultimo = m.index + bruto.length;
  }
  if (ultimo < texto.length) partes.push(texto.slice(ultimo));
  return partes;
}

export function EditorMensagem({
  chave,
  aberto,
  onFechar,
  onSalvou,
  temGrupoTeste,
}: {
  chave: string | null;
  aberto: boolean;
  onFechar: () => void;
  onSalvou: () => void;
  temGrupoTeste: boolean;
}) {
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [texto, setTexto] = useState('');
  /**
   * 🔴 O SEGUNDO CAMPO (mig 580): a linha que o sistema repete por
   * equipamento. Não é um bloco de repetição dentro do texto — é um campo
   * separado, com as suas próprias fichas.
   */
  const [linhaTexto, setLinhaTexto] = useState('');
  const [linhaVolta, setLinhaVolta] = useState('');
  const [teto, setTeto] = useState(10);
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const areaRef = useRef<HTMLTextAreaElement>(null);
  const linhaRef = useRef<HTMLInputElement>(null);
  const voltaRef = useRef<HTMLInputElement>(null);
  /**
   * 🔴 QUAL CAMPO DA LINHA RECEBE A FICHA (frente 7 do adendo de 11/09).
   *
   * O bloco da linha tem DOIS campos ("como listar cada equipamento" e "…e na
   * mensagem de voltou ao ar") e UM conjunto de fichas. Antes, a ficha caía
   * sempre no primeiro e sempre no FIM do texto: quem estava escrevendo o
   * campo do retorno via a variável aparecer no campo de cima.
   *
   * ⚠️ A lembrança vive no último campo que teve foco — não no que está
   * focado AGORA, porque clicar na ficha já tira o foco do campo. Por isso
   * `onFocus` guarda, e o clique da ficha usa o que ficou guardado.
   */
  const [campoDaLinha, setCampoDaLinha] = useState<'linha' | 'volta'>('linha');
  /** O texto de cada bloco extra, por chave. */
  const [blocos, setBlocos] = useState<Record<string, string>>({});
  const blocoRefs = useRef<Record<string, HTMLTextAreaElement | null>>({});
  /** ⚠️ Mesma regra da frente 7: a ficha vai para o bloco que teve o foco. */
  const [blocoFocado, setBlocoFocado] = useState<string | null>(null);

  const carregar = useCallback(() => {
    if (!chave) return;
    setCarregando(true);
    setErro(null);
    setAviso(null);
    api
      .get<Detalhe>(`/api/v1/admin/whatsapp/gatilhos/${chave}`)
      .then((d) => {
        setDetalhe(d);
        setTexto(d.template);
        setLinhaTexto(d.linha?.atual ?? '');
        setLinhaVolta(d.linha?.atualVolta ?? '');
        setTeto(d.linha?.teto ?? 10);
        setBlocos(Object.fromEntries((d.blocos ?? []).map((b) => [b.chave, b.atual])));
      })
      .catch((e: Error) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [chave]);

  useEffect(() => {
    if (aberto) carregar();
  }, [aberto, carregar]);

  /**
   * O motor ÚNICO de inserção: põe a variável onde o cursor está, no campo
   * indicado — e devolve o foco para lá, para continuar escrevendo.
   *
   * ⚠️ Um motor só para os três campos. Antes havia dois jeitos de inserir (um
   * com cursor, outro que concatenava no fim), e o segundo era o que errava.
   */
  function inserirNoCampo(
    alvo: HTMLTextAreaElement | HTMLInputElement | null,
    valorAtual: string,
    aplicar: (novo: string) => void,
    v: string,
  ) {
    const marca = `{{${v}}}`;
    if (!alvo) {
      aplicar(valorAtual + marca);
      return;
    }
    const inicio = alvo.selectionStart ?? valorAtual.length;
    const fim = alvo.selectionEnd ?? valorAtual.length;
    aplicar(valorAtual.slice(0, inicio) + marca + valorAtual.slice(fim));
    requestAnimationFrame(() => {
      alvo.focus();
      alvo.setSelectionRange(inicio + marca.length, inicio + marca.length);
    });
  }

  /** A ficha da MENSAGEM — sempre no corpo, que é o campo dela. */
  function inserir(v: string) {
    inserirNoCampo(areaRef.current, texto, setTexto, v);
  }

  /** A ficha de um BLOCO extra — no cursor, no campo dele. */
  function inserirNoBloco(bloco: string, v: string) {
    const alvo = blocoFocado === bloco ? blocoRefs.current[bloco] : null;
    inserirNoCampo(
      alvo ?? blocoRefs.current[bloco],
      blocos[bloco] ?? '',
      (novo) => setBlocos((atual) => ({ ...atual, [bloco]: novo })),
      v,
    );
  }

  /** A ficha do EQUIPAMENTO — no campo da linha que estava sendo escrito. */
  function inserirNaLinha(v: string) {
    if (campoDaLinha === 'volta') {
      inserirNoCampo(voltaRef.current, linhaVolta, setLinhaVolta, v);
      return;
    }
    inserirNoCampo(linhaRef.current, linhaTexto, setLinhaTexto, v);
  }

  /**
   * A prévia, montada no navegador enquanto se digita.
   *
   * 🔴 A lista de equipamentos entra MONTADA (mig 580): com a linha
   * configurada, são três equipamentos de exemplo com horários DIFERENTES —
   * sem isso ele não vê que cada um tem o seu.
   */
  const previa = detalhe
    ? texto.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (todo, nome: string) => {
        if (detalhe.linha && (nome === 'equipamento' || nome === 'equipamentos')) {
          return listaDeExemplo(linhaTexto);
        }
        const v = detalhe.variaveis.find((x) => x.chave === nome);
        return v ? v.exemplo : todo;
      })
    : '';

  const desconhecidas = detalhe
    ? [...new Set([...texto.matchAll(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g)].map((m) => m[1]))].filter(
        (u) => !detalhe.variaveis.some((v) => v.chave === u),
      )
    : [];

  async function salvar() {
    if (!chave) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await api.put(`/api/v1/admin/whatsapp/gatilhos/${chave}/template`, { template: texto });
      // Os dois campos são guardados no mesmo gesto (mig 580) — a linha só
      // existe nos avisos que listam equipamento.
      if (detalhe?.linha) {
        await api.put(`/api/v1/admin/whatsapp/gatilhos/${chave}/linha`, {
          linha: linhaTexto,
          ...(detalhe.linha.atualVolta !== null ? { volta: linhaVolta } : {}),
          teto,
        });
      }
      for (const b of detalhe?.blocos ?? []) {
        await api.put(`/api/v1/admin/whatsapp/gatilhos/${chave}/bloco/${b.chave}`, {
          texto: blocos[b.chave] ?? '',
        });
      }
      setAviso('Texto guardado. Isso não liga o aviso — ele continua como estava.');
      onSalvou();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
      areaRef.current?.focus();
    } finally {
      setSalvando(false);
    }
  }

  /**
   * 🔴 A ÚNICA mensagem que esta rodada autoriza disparar — e só para o grupo
   * de teste escolhido à mão.
   */
  async function testar() {
    if (!chave) return;
    setTestando(true);
    setErro(null);
    setAviso(null);
    try {
      // ⚠️ Salva antes: testar um texto diferente do guardado confere a coisa errada.
      await api.put(`/api/v1/admin/whatsapp/gatilhos/${chave}/template`, { template: texto });
      const r = await api.post<{ detalhe: string }>('/api/v1/admin/whatsapp/teste', { gatilho: chave });
      setAviso(r.detalhe);
      onSalvou();
    } catch (err) {
      const e = err as Error & { response?: { erro?: string } };
      setErro(e.response?.erro ?? e.message);
    } finally {
      setTestando(false);
    }
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()} size="lg">
      <DialogHeader>
        <DialogTitle>{detalhe?.nome ?? 'Mensagem'}</DialogTitle>
        <DialogDescription>{detalhe?.descricao}</DialogDescription>
      </DialogHeader>

      <DialogContent className="space-y-4">
        {carregando && <p className="text-sm text-ink-3 dark:text-ink-dark-3">Carregando…</p>}
        {erro && <Alert variant="danger" data-testid="editor-erro">{erro}</Alert>}
        {aviso && <Alert variant="info" data-testid="editor-aviso">{aviso}</Alert>}

        {detalhe && (
          <>
            {/* 🔴 Frente 9: o destino REAL, não o destino de sempre. */}
            {detalhe.soInterno && (
              <Alert variant="info" data-testid="editor-so-interno">
                Este é um alarme da IGM: ele vai para o <b>grupo interno de supervisão</b> e{' '}
                <b>nunca para o grupo de um cliente</b> — a não ser que você libere um cliente
                específico na ficha dele. Escreva para a equipe.
              </Alert>
            )}

            {detalhe.ligado && (
              <Alert variant="warning" data-testid="editor-esta-ligado">
                Este aviso está ligado. O que você guardar aqui passa a valer no próximo caso
                {detalhe.soInterno ? '.' : ' — e vai para o grupo do cliente.'}
              </Alert>
            )}

            <div>
              <label htmlFor="editor-texto" className="mb-1.5 block text-sm text-ink-2 dark:text-ink-dark-2">
                A mensagem
              </label>
              <textarea
                id="editor-texto"
                ref={areaRef}
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                rows={7}
                placeholder="Escreva a mensagem. Clique nas variáveis abaixo para inserir os dados do caso."
                className="w-full rounded-lg border border-line bg-paper-surface px-3 py-2 text-sm text-ink-1 focus:border-red focus:outline-none dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
                data-testid="editor-texto"
              />
              <p className="mt-1 text-xs text-ink-3 dark:text-ink-dark-3">
                *negrito*, _itálico_ e ~riscado~ funcionam no WhatsApp.
              </p>
            </div>

            {/* ─── As variáveis, por clique ─── */}
            <div>
              <p className="mb-1.5 text-sm text-ink-2 dark:text-ink-dark-2">
                Dados que este aviso conhece — clique para inserir
              </p>
              <div className="flex flex-wrap gap-2" data-testid="editor-variaveis">
                {detalhe.variaveis.map((v) => (
                  <button
                    key={v.chave}
                    type="button"
                    onClick={() => inserir(v.chave)}
                    className="rounded-full border border-line px-3 py-1 text-xs text-ink-2 hover:border-red hover:text-red dark:border-line-dark dark:text-ink-dark-2"
                    title={`Exemplo: ${v.exemplo}`}
                  >
                    {v.rotulo}
                  </button>
                ))}
              </div>
            </div>

            {/* ─── 🔴 COMO LISTAR CADA EQUIPAMENTO (mig 580) ─── */}
            {detalhe.linha && (
              <div
                className="space-y-3 rounded-lg border border-line p-3 dark:border-line-dark"
                data-testid="editor-linha"
              >
                <div>
                  <label
                    htmlFor="editor-linha-texto"
                    className="mb-1.5 flex items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2"
                  >
                    Como listar cada equipamento
                    <HelpTip texto="Quando vários equipamentos entram na mesma mensagem, o sistema repete esta linha — uma para cada um, com os dados dele. Escreva UMA linha só: as fichas abaixo valem por equipamento. Deixe em branco para voltar ao formato antigo, em que os nomes saem numa frase só (CÂMERA 01, CÂMERA 02 e DVR)." />
                  </label>
                  <Input
                    id="editor-linha-texto"
                    ref={linhaRef}
                    value={linhaTexto}
                    onChange={(e) => setLinhaTexto(e.target.value)}
                    onFocus={() => setCampoDaLinha('linha')}
                    placeholder="Ex.: {{equipamento}} - {{desde}}"
                    className="font-mono"
                    data-testid="editor-linha-texto"
                  />
                </div>

                {detalhe.linha.atualVolta !== null && (
                  <div>
                    <label
                      htmlFor="editor-linha-volta"
                      className="mb-1.5 flex items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2"
                    >
                      …e na mensagem de “voltou ao ar”
                      <HelpTip texto="O retorno diz outra coisa que a queda — em vez de “fora desde”, quanto tempo ficou fora. Por isso a linha é separada. Em branco, o retorno continua saindo como hoje." />
                    </label>
                    <Input
                      id="editor-linha-volta"
                      ref={voltaRef}
                      value={linhaVolta}
                      onChange={(e) => setLinhaVolta(e.target.value)}
                      onFocus={() => setCampoDaLinha('volta')}
                      placeholder="Ex.: {{equipamento}} — ficou fora {{duracao}}"
                      className="font-mono"
                      data-testid="editor-linha-volta"
                    />
                  </div>
                )}

                {/* As fichas DESTE campo — só o que muda de um equipamento
                    para outro. O nome do cliente não entra: repetiria em
                    toda linha, e o servidor recusa.

                    ⚠️ Frente 7: a etiqueta DIZ em qual dos dois campos a ficha
                    vai cair, e muda junto com o foco. Antes ela caía sempre no
                    de cima, em silêncio. */}
                <div>
                  <p className="mb-1.5 text-xs text-ink-3 dark:text-ink-dark-3">
                    Dados de cada equipamento — clique para inserir{' '}
                    {detalhe.linha.atualVolta !== null && (
                      <span data-testid="editor-linha-destino">
                        em <b>{campoDaLinha === 'volta' ? '“voltou ao ar”' : '“como listar”'}</b>
                      </span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2" data-testid="editor-linha-variaveis">
                    {detalhe.linha.variaveis.map((v) => (
                      <button
                        key={v.chave}
                        type="button"
                        onClick={() => inserirNaLinha(v.chave)}
                        className="rounded-full border border-line px-3 py-1 text-xs text-ink-2 hover:border-red hover:text-red dark:border-line-dark dark:text-ink-dark-2"
                        title={`Exemplo: ${v.exemplo}`}
                      >
                        {v.rotulo}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 🔴 O teto: vinte equipamentos seriam vinte linhas no grupo
                    do condomínio. Quem decide o limite é o Matheus. */}
                <div className="flex flex-wrap items-center gap-2">
                  <label htmlFor="editor-linha-teto" className="text-xs text-ink-3 dark:text-ink-dark-3">
                    Mostrar no máximo
                  </label>
                  <Input
                    id="editor-linha-teto"
                    type="number"
                    min={1}
                    max={200}
                    value={String(teto)}
                    onChange={(e) => setTeto(Math.max(1, Number(e.target.value) || 1))}
                    className="w-20"
                    data-testid="editor-linha-teto"
                  />
                  <span className="text-xs text-ink-3 dark:text-ink-dark-3">
                    equipamentos por mensagem; o resto vira “e mais X equipamentos”.
                  </span>
                </div>
              </div>
            )}

            {/* ─── 🔴 OS BLOCOS EXTRAS (adendo 11/09) ───
                Texto que entra na mensagem SÓ quando o caso acontece. Vazio
                desliga — e a tela diz isso, porque um campo em branco sem
                explicação parece esquecimento, não escolha. */}
            {(detalhe.blocos ?? []).map((b) => (
              <div
                key={b.chave}
                className="space-y-2 rounded-lg border border-line p-3 dark:border-line-dark"
                data-testid={`editor-bloco-${b.chave}`}
              >
                <label
                  htmlFor={`editor-bloco-campo-${b.chave}`}
                  className="flex items-center gap-2 text-sm text-ink-2 dark:text-ink-dark-2"
                >
                  {b.rotulo}
                  <HelpTip texto={b.ajuda} />
                </label>
                <textarea
                  id={`editor-bloco-campo-${b.chave}`}
                  ref={(el) => {
                    blocoRefs.current[b.chave] = el;
                  }}
                  value={blocos[b.chave] ?? ''}
                  onChange={(e) => setBlocos((v) => ({ ...v, [b.chave]: e.target.value }))}
                  onFocus={() => setBlocoFocado(b.chave)}
                  rows={2}
                  placeholder="Em branco, este trecho não aparece em mensagem nenhuma."
                  className="w-full rounded-lg border border-line bg-paper-surface px-3 py-2 text-sm text-ink-1 focus:border-red focus:outline-none dark:border-line-dark dark:bg-paper-dark-surface dark:text-ink-dark-1"
                  data-testid={`editor-bloco-texto-${b.chave}`}
                />
                {b.variaveis.length > 0 && (
                  <div>
                    <p className="mb-1.5 text-xs text-ink-3 dark:text-ink-dark-3">
                      Dados deste trecho — clique para inserir
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {b.variaveis.map((v) => (
                        <button
                          key={v.chave}
                          type="button"
                          onClick={() => inserirNoBloco(b.chave, v.chave)}
                          className="rounded-full border border-line px-3 py-1 text-xs text-ink-2 hover:border-red hover:text-red dark:border-line-dark dark:text-ink-dark-2"
                          title={`Exemplo: ${v.exemplo}`}
                        >
                          {v.rotulo}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-ink-3 dark:text-ink-dark-3">
                  {(blocos[b.chave] ?? '').trim()
                    ? 'Ligado: este trecho entra quando o caso acontecer.'
                    : 'Desligado: nada será acrescentado à mensagem.'}
                </p>
              </div>
            ))}

            {/* ⚠️ Variável inventada: acusada aqui, e recusada no servidor. */}
            {desconhecidas.length > 0 && (
              <Alert variant="danger" data-testid="editor-desconhecidas">
                {desconhecidas.length === 1 ? 'A variável ' : 'As variáveis '}
                {desconhecidas.map((d) => `{{${d}}}`).join(', ')}
                {desconhecidas.length === 1 ? ' não existe' : ' não existem'} neste aviso. Use as
                da lista acima — o que não existe chegaria em branco{' '}
                {detalhe.soInterno ? 'na mensagem.' : 'no grupo do cliente.'}
              </Alert>
            )}

            {/* ─── 🔴 A PRÉVIA ─── */}
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span className="text-sm text-ink-2 dark:text-ink-dark-2">
                  {detalhe.soInterno ? 'Como a equipe vê' : 'Como o cliente vê'}
                </span>
                <Badge variant="neutral" size="sm">Dados de exemplo</Badge>
              </div>
              <div
                className="whitespace-pre-wrap rounded-lg bg-[#dcf8c6] p-3 text-sm text-[#111b21] dark:bg-[#005c4b] dark:text-white"
                data-testid="editor-previa"
              >
                {previa.trim() ? comFormatacao(previa) : 'A mensagem está vazia — e vazia não dispara.'}
              </div>
            </div>
          </>
        )}
      </DialogContent>

      <DialogFooter>
        <Button variant="ghost" onClick={onFechar}>
          Fechar
        </Button>
        {/* 🔴 O teste só existe com grupo de teste escolhido. */}
        <Button
          variant="secondary"
          loading={testando}
          disabled={!temGrupoTeste || !texto.trim() || desconhecidas.length > 0}
          onClick={() => void testar()}
          data-testid="editor-enviar-teste"
          title={
            temGrupoTeste
              ? 'Manda esta mensagem para o grupo de teste, com os dados de exemplo.'
              : 'Escolha o grupo de teste antes — sem ele, não há para onde mandar.'
          }
        >
          Enviar teste
        </Button>
        {/* ⚠️ Frente 9: o botão JÁ nascia travado com variável inventada (e o
            servidor recusa de novo, por baixo) — o que faltava era dizer por
            quê. Botão apagado sem motivo parece defeito da tela. */}
        <Button
          loading={salvando}
          disabled={desconhecidas.length > 0}
          onClick={() => void salvar()}
          data-testid="editor-salvar"
          title={
            desconhecidas.length > 0
              ? 'Não dá para guardar enquanto o texto usar uma variável que este aviso não conhece — corrija o que está marcado em vermelho acima.'
              : undefined
          }
        >
          Guardar texto
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
